import {
    type MappedProductCollectionModel,
    type IProductCollectionModel,
    type ProductCollectionModelBase,
} from "./models";

export function* flattenAllCollections<
    TProductCollectionModel extends ProductCollectionModelBase &
        IProductCollectionModel<TProductCollectionModel>
>(
    collections: TProductCollectionModel[] | null | undefined
): Generator<TProductCollectionModel, void, unknown> {
    for (const { collection } of flattenAllCollectionsWithParents(
        collections
    )) {
        yield collection;
    }
}

export function* flattenAllCollectionsWithDepth<
    TProductCollectionModel extends ProductCollectionModelBase &
        IProductCollectionModel<TProductCollectionModel>
>(
    collections: TProductCollectionModel[] | null | undefined
): Generator<
    { collection: TProductCollectionModel; depth: number },
    void,
    unknown
> {
    for (const { collection, parents } of flattenAllCollectionsWithParents(
        collections
    )) {
        yield { collection, depth: parents.length };
    }
}

interface CollectionItemWithParents<
    TProductCollectionModel extends ProductCollectionModelBase &
        IProductCollectionModel<TProductCollectionModel>
> {
    collection: TProductCollectionModel;
    parents: TProductCollectionModel[];
}

export function* flattenAllCollectionsWithParents<
    TProductCollectionModel extends ProductCollectionModelBase &
        IProductCollectionModel<TProductCollectionModel>
>(
    collections: TProductCollectionModel[] | undefined | null,
    parents: TProductCollectionModel[] | undefined = undefined
): Generator<
    CollectionItemWithParents<TProductCollectionModel>,
    void,
    unknown
> {
    if (collections == null) return;
    if (parents == null) {
        parents = [];
    }
    for (const collection of collections) {
        yield { collection, parents };
        for (const subSet of flattenAllCollectionsWithParents(
            collection.SubCollections,
            parents.concat([collection])
        )) {
            yield subSet;
        }
    }
}

export function getCollectionLevel<
    TProductCollectionModel extends ProductCollectionModelBase &
        IProductCollectionModel<TProductCollectionModel>
>(
    item: TProductCollectionModel,
    root: TProductCollectionModel[] | null | undefined
) {
    const match = Array.from(flattenAllCollectionsWithParents(root)).find(
        (x) => x.collection.Id == item.Id
    );
    if (match == null) throw new Error("Could not match item");
    return match.parents.length;
}

export function findCollectionById<
    TProductCollectionModel extends ProductCollectionModelBase &
        IProductCollectionModel<TProductCollectionModel>
>(collectionId: number, mappedRoot: TProductCollectionModel[] | undefined) {
    for (const collection of flattenAllCollections(mappedRoot)) {
        if (collection.Id == collectionId) return collection;
    }
    return undefined;
}

export function* iterateUp(
    model: MappedProductCollectionModel
): Generator<MappedProductCollectionModel> {
    yield model;
    if (model.parent != null) {
        for (const parent of iterateUp(model.parent)) {
            yield parent;
        }
    }
}
